import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import MailLink from '../components/MailLink';
import { SITE, mailto } from '../content/site';
import './DeleteAccount.css';

const STEPS = [
  {
    title: 'In the app, if it has an account',
    body:
      'Open the app, go to Settings or Profile, then Delete account. Confirm when asked. The request runs immediately and cannot be undone.',
  },
  {
    title: 'By email, if the app is already gone',
    body:
      'Use the form below. It opens your mail app with everything we need already written out — send it from the address you signed up with so we can confirm the request is yours.',
  },
  {
    title: 'If the app never had an account',
    body:
      'Most of our apps keep everything on your device and nothing on a server. Uninstalling the app, or clearing its storage in Android Settings, removes that data permanently. There is nothing for us to delete.',
  },
];

function DeleteAccount() {
  const [app, setApp] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = app.trim().length > 1 && emailValid;

  const href = useMemo(() => {
    const subject = `Delete my account — ${app.trim() || 'AppNexa app'}`;
    const body = [
      'Hello AppNexa,',
      '',
      'Please delete my account and the data attached to it.',
      '',
      `App: ${app.trim()}`,
      `Account email: ${email.trim()}`,
      note.trim() ? `Note: ${note.trim()}` : '',
      '',
      'I understand this cannot be undone.',
    ]
      .filter(Boolean)
      .join('\n');
    return mailto(subject, body);
  }, [app, email, note]);

  return (
    <main id="main" className="delete">
      <div className="delete-hero">
        <div className="delete-glow" aria-hidden="true" />
        <div className="wrap">
          <nav className="crumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Delete your account</span>
          </nav>
          <h1>
            Delete your account <span className="gradient-text">and data</span>
          </h1>
          <p className="legal-meta">
            Last updated {SITE.legalUpdated} · Applies to all {SITE.name} apps
          </p>
          <p className="legal-lede">
            One request, no retention offer, no support maze. Here are the three
            routes, depending on how the app stores your data.
          </p>
        </div>
      </div>

      <section className="delete-steps">
        <div className="wrap grid-3">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 80} className="step-card card-surface">
              <span className="step-num">{i + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="delete-form-section">
        <div className="wrap delete-grid">
          <Reveal className="delete-form card-surface">
            <h2>Send the request</h2>
            <p className="form-help">
              Nothing is submitted to a server from this page — it writes the
              email for you and your own mail app sends it.
            </p>

            <label htmlFor="app">
              Which app?
              <input
                id="app"
                value={app}
                onChange={(e) => setApp(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="The app's name as it appears on your phone"
                autoComplete="off"
              />
            </label>

            <label htmlFor="email">
              Account email
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="the address you signed in with"
                autoComplete="email"
              />
              {touched && email && !emailValid ? (
                <span className="err">That does not look like an email address.</span>
              ) : null}
            </label>

            <label htmlFor="note">
              Anything we should know <em>(optional)</em>
              <textarea
                id="note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A second address on the account, a purchase to check, anything else"
              />
            </label>

            <a
              className={ready ? 'btn' : 'btn disabled'}
              href={ready ? href : undefined}
              aria-disabled={!ready}
              onClick={(e) => {
                if (!ready) {
                  e.preventDefault();
                  setTouched(true);
                }
              }}
            >
              Open my mail app
            </a>

            <p className="form-foot">
              Prefer to write it yourself?{' '}
              <MailLink subject="Delete my account">Open a blank email</MailLink>{' '}
              and include the app name and your account address.
            </p>
          </Reveal>

          <div className="delete-facts">
            <Reveal className="fact card-surface">
              <h3>What gets deleted</h3>
              <ul>
                <li>Your account and the sign-in identity behind it — name, email address, profile picture</li>
                <li>Content you saved into the account and anything synced from it</li>
                <li>Preferences, history and progress held against the account</li>
                <li>Support correspondence, once the matter is closed</li>
              </ul>
            </Reveal>

            <Reveal delay={90} className="fact card-surface">
              <h3>What we cannot delete</h3>
              <p>
                Purchase and subscription records belong to Google Play and are
                kept under their policy and the tax law that applies to them.
                Aggregate crash and usage counts that no longer identify anyone
                stay in Google&apos;s consoles on their own schedule.
              </p>
            </Reveal>

            <Reveal delay={160} className="fact card-surface accent">
              <h3>How long it takes</h3>
              <p>
                We confirm the request comes from you, then complete it within
                30 days — usually the same working day. You get an email when it
                is done.
              </p>
            </Reveal>

            <Reveal delay={230} className="fact card-surface warn">
              <h3>Cancel a subscription first</h3>
              <p>
                Deleting an account does not cancel a paid plan. End it in
                Google Play before you delete, or it keeps renewing.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="delete-foot">
        <div className="wrap">
          <p>
            Related: <Link to="/privacy">privacy policy</Link> ·{' '}
            <Link to="/terms">terms of service</Link> ·{' '}
            <Link to="/contact">contact us</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

export default DeleteAccount;
